import { type ChatMessage, MAX_CHAT_MESSAGE_LENGTH } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Textarea } from "@loot-os/ui/components/textarea";
import { useNavigate } from "@tanstack/react-router";
import { SendHorizontal } from "lucide-react";
import { useState } from "react";

import {
  muteUntilLabel,
  toUserFacingChatError,
  useChatRulesAgreed,
  useSendChatMessage,
} from "../../lib/chat.js";
import { UsernameDialog } from "../UsernameDialog.js";
import { ChatRulesDialog } from "./ChatRulesDialog.js";
import { ReplyIndicator } from "./ReplyPreview.js";

/**
 * The message box, behind boxed's gates in order: sign in, choose a username (the name everyone
 * sees), agree to the rules, not muted. Enter sends, Shift+Enter breaks the line. Nothing is shown
 * optimistically: the text clears on send and comes back if the server refused it.
 */
export function ChatComposer({
  roomId,
  signedIn,
  sessionReady,
  username,
  muted,
  mutedUntil,
  replyingTo,
  onCancelReply,
}: {
  roomId: string;
  signedIn: boolean;
  /** The session token is minted and the viewer's profile loaded. */
  sessionReady: boolean;
  username: string | null;
  muted: boolean;
  mutedUntil: string | undefined;
  replyingTo: ChatMessage | null;
  onCancelReply: () => void;
}) {
  const navigate = useNavigate();
  const send = useSendChatMessage(roomId);
  const [rulesAgreed, agreeToRules] = useChatRulesAgreed();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [usernameOpen, setUsernameOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  if (!signedIn) {
    return (
      <Gate>
        <Button className="w-full" onClick={() => navigate({ to: "/login" })}>
          Sign in to chat
        </Button>
      </Gate>
    );
  }
  if (!sessionReady) {
    return (
      <Gate>
        <p className="text-center text-sm text-muted-foreground">Connecting…</p>
      </Gate>
    );
  }
  if (!username) {
    return (
      <Gate>
        <p className="text-center text-xs text-muted-foreground">
          Other players see your username in chat.
        </p>
        <Button className="w-full" onClick={() => setUsernameOpen(true)}>
          Choose a username
        </Button>
        {usernameOpen && (
          <UsernameDialog open={usernameOpen} onOpenChange={setUsernameOpen} username={null} />
        )}
      </Gate>
    );
  }
  if (!rulesAgreed) {
    return (
      <Gate>
        <Button className="w-full" onClick={() => setRulesOpen(true)}>
          Read the rules to chat
        </Button>
        <ChatRulesDialog open={rulesOpen} onOpenChange={setRulesOpen} onAgree={agreeToRules} />
      </Gate>
    );
  }
  if (muted) {
    const until = muteUntilLabel(mutedUntil);
    return (
      <Gate>
        <p className="text-center text-sm text-muted-foreground">
          {until ? `You are muted until ${until}.` : "You have been muted in chat."}
        </p>
      </Gate>
    );
  }

  const submit = () => {
    const text = draft.trim();
    if (!text || send.isPending) return;
    setError(null);
    setDraft("");
    send.mutate(
      { text, ...(replyingTo ? { replyToMessageId: replyingTo.messageId } : {}) },
      {
        onSuccess: () => onCancelReply(),
        onError: (err) => {
          setDraft((current) => current || text);
          setError(toUserFacingChatError(err));
        },
      },
    );
  };

  return (
    <div className="grid gap-2 p-3">
      {replyingTo && (
        <ReplyIndicator displayName={replyingTo.displayName} onCancel={onCancelReply} />
      )}
      {error && <p className="px-1 text-xs text-destructive">{error}</p>}
      <form
        className="flex min-h-[62px] flex-col rounded-md border border-input bg-foreground/5 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Textarea
          value={draft}
          maxLength={MAX_CHAT_MESSAGE_LENGTH}
          rows={1}
          placeholder="Type something..."
          aria-label="Chat message"
          className="max-h-28 min-h-9 resize-none border-0 bg-transparent px-5 pt-3 pb-0 text-sm font-medium shadow-none focus-visible:ring-0 dark:bg-transparent"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <div className="flex items-center justify-end gap-2 px-2 pb-1.5">
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {draft.length}/{MAX_CHAT_MESSAGE_LENGTH}
          </span>
          <Button
            type="submit"
            size="icon-xs"
            variant="ghost"
            disabled={!draft.trim() || send.isPending}
            aria-label="Send message"
          >
            <SendHorizontal />
          </Button>
        </div>
      </form>
    </div>
  );
}

function Gate({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-2 p-3">{children}</div>;
}
